import { useInfiniteQuery } from '@tanstack/react-query';

import { FolderGateway } from '../gateways';
import { buildFolderEntity, handleResponse } from '@/core/utils';
import { folderKeyFactory } from '@/core/query-keys';
import type { Folder, RawFolder } from '@/core/types';
import { useCurrentUser } from '@/core/hooks/use-current-user';

interface QueryProps {
  pageParam?: number;
}

interface PagedArchivedFoldersPage {
  entities: RawFolder[];
  nextPage: number | null;
  previousPage: number | null;
}

interface PagedArchivedFoldersResult {
  entities: Folder[];
  nextPage: number | null;
  previousPage: number | null;
}

async function fetchPagedArchivedFolders({
  pageParam
}: QueryProps): Promise<PagedArchivedFoldersPage> {
  const { folders, nextPage, previousPage } = await handleResponse(
    FolderGateway.fetchArchived({ pageParam })
  );
  return { entities: folders, nextPage, previousPage };
}

export function useFetchArchivedFolders({ options = {} } = {}) {
  const { data: user } = useCurrentUser();

  return useInfiniteQuery<PagedArchivedFoldersPage, Error, PagedArchivedFoldersResult>(
    folderKeyFactory.archived(),
    ({ pageParam = 1 }) => fetchPagedArchivedFolders({ pageParam }),
    {
      getNextPageParam: (lastPage) => lastPage.nextPage,
      select: (data) => ({
        ...data,
        pages: data.pages.map((page) => ({
          ...page,
          entities: page.entities.map((folder) => buildFolderEntity(folder, user?.userId))
        }))
      }),
      enabled: !!user,
      ...options
    }
  );
}
